import React, { useContext, useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';
import colorsBackground from 'images/colors-bg.webp';
import thoughtBubbles from 'images/thought-bubbles.webp';
import {
  OnboardingContext,
  OnboardingWrapper,
} from 'registration/containers/Onboarding';

import { Stack } from 'shared/components/ihcl/patterns';
import { styled } from 'shared/components/ihcl/styled';
import { Text } from 'shared/components/ihcl/text';
import { LoadingDots } from 'shared/components/ihcl/v2/loading-dots';
import { doPost } from 'shared/helpers/http';
import { trackComponentView, trackEvent } from 'shared/helpers/tracking';

import { useReserveNextScreening } from 'api/talent/hooks/screenings';

const updates = {
  talent_tags: ['onboard_schedule_screen_sept2021'],
};

const Background = styled('div', ({ $theme }) => ({
  backgroundImage: `url(${colorsBackground})`,
  backgroundSize: '100% 100%',
  filter: `blur(${$theme.sizing.unit40})`,
  opacity: '30%',
  position: 'absolute',
  width: '100%',
  height: '100lvh',
  left: '0',
  top: '0',
}));

const Content = styled('div', ({ $theme }) => ({
  display: 'flex',
  gap: $theme.sizing.unit48,
  position: 'relative',
  textAlign: 'center',
  alignItems: 'center',
  height: '100lvh',
}));

const ThoughtBubbles = styled('img', {
  width: '100%',
});

type DefaultScheduledScreeningProps = {
  nextAction: string;
};

export const DefaultScheduledScreening = ({
  nextAction,
}: DefaultScheduledScreeningProps) => {
  const { onboardingData: talentProfile } = useContext(OnboardingContext);

  const { mutate: onSend } = useMutation({
    mutationFn: () =>
      doPost(`/talent_profile/${talentProfile.id}/tag`, updates),
    onMutate: () => {
      trackEvent('talent.screening.default.send_update.start');
    },
    onError: (error) => {
      console.error(error);
    },
    onSettled: () => {
      setTimeout(() => {
        trackEvent('talent.screening.default.send_update.complete');
        window.location.assign(new URL(nextAction, window.location.origin));
      }, 3000);
    },
  });

  const { mutate: reserveNextScreening } = useReserveNextScreening({
    onError: () => {
      console.error(`Error reserving next screening for ${talentProfile.id}`);
    },
    onSettled: () => onSend(),
  });

  useEffect(() => {
    trackComponentView('talent.screening.default.transition');
    if (!talentProfile?.screening) {
      reserveNextScreening();
    } else {
      onSend();
    }
  }, [onSend, reserveNextScreening, talentProfile?.screening]);

  useEffect(() => {
    const timer = setTimeout(() => {
      trackEvent('talent.screening.default.extended_loading');
    }, 6000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <OnboardingWrapper $compactMaxWidth>
        <Background />
        <Content>
          <Stack
            $flexDirection="column"
            $gapSizing="unit48"
            $alignItems="center"
          >
            <ThoughtBubbles alt="thought-bubbles" src={thoughtBubbles} />
            <Text variant="HeadingSmall">
              Let&apos;s find a time to talk — because you&apos;re more than
              your resume.
            </Text>
            <LoadingDots className="text-accent30" />
          </Stack>
        </Content>
      </OnboardingWrapper>
    </>
  );
};

export default DefaultScheduledScreening;
